/**
 * 数据源=真实（live）的拉数逻辑：本机 token 持续轮询，合并进编辑器 liveData 并刷新 3D 信息卡。
 * 这两个函数是「内部实现细节」，只由数据源状态的变更统一调用（见 SceneCanvas.applyDataSourceMode）；
 * 任何 UI 组件都不应再直接调用它们——这正是 docs/06 §3.4 消除 D1（进出预览静默掐断真实会话）的关键。
 */
import { useSceneStore } from '@/stores/scene'
import { useConnectDataEditorStore } from '@/stores/connectDataEditor'
import { startConnectDataPollLoops, createMergingLiveDataSink } from '@/runtime/source-gateway/connectDataPoll'
import { computeLiveDataPatches } from '@/runtime/liveData/computeLiveDataPatches'
import { applyRuntimePatches } from '@/three/ObjectManager'
import { mergeDeepLiveData } from '@/utils/liveDataPaths'

export type StartViewLiveDataResult =
  | { ok: true }
  | { ok: false; reason: 'not-logged-in' | 'no-bound-devices' }

export function startViewLiveData(logTag = '[view-live-data]'): StartViewLiveDataResult {
  const sceneStore = useSceneStore()
  const editorStore = useConnectDataEditorStore()
  const session = sceneStore.getConnectRuntimeSession()
  const scene = sceneStore.activeScene()
  if (!session || !scene) return { ok: false, reason: 'not-logged-in' }
  const sink = createMergingLiveDataSink((merged) => {
    const ov = sceneStore.getPreviewLiveDataOverrides?.() ?? {}
    const withOv =
      ov && typeof ov === 'object' && Object.keys(ov).length > 0
        ? mergeDeepLiveData(merged, ov as Record<string, unknown>)
        : merged
    sceneStore.setEditorPreviewMergedLiveData(withOv)
    // 留下真数种子：切回 Mock 时按此抖动，而不是 Math.random 凭空捏造
    sceneStore.rememberLiveDataSeed(withOv)
    const liveScene = sceneStore.activeScene()
    if (liveScene) applyRuntimePatches(computeLiveDataPatches(liveScene, withOv))
  })
  const loops = startConnectDataPollLoops({
    scene,
    session,
    pollMs: sceneStore.getProjectConnect().pollIntervalMs || 3000,
    viewer: sink,
    logTag,
    // 编辑器由 HtmlOverlayHost 推规范化切片；此处再派发会缺 status 别名并与宿主竞态
    dispatchLiveDataEvent: false,
  })
  if (loops.length === 0) return { ok: false, reason: 'no-bound-devices' }
  editorStore.setPollHandles(loops)
  return { ok: true }
}

export function stopViewLiveData(): void {
  useConnectDataEditorStore().stopPoll()
}
